// (c) chaehongee. Confidential and proprietary.

import {
    useEffect,
    useMemo,
    useState,
    type KeyboardEvent,
    type ReactElement,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
    X as CloseIcon,
    User as UserIcon,
    Trash2 as TrashIcon,
    RefreshCw as RefreshIcon,
} from 'lucide-react'
import type { LoraCollaboratorsResponse } from 'models-ts/lora'
import { PrivacyToggle } from '../../StatusBar/PrivacyToggle'
import {
    getLoraCollaborators,
    shareLora,
    unshareLora,
    updateLoraPrivacy,
} from '../../../services/loras'
import type { HttpError } from '../../../services/http'
import styles from '../../StatusBar/ShareModal.module.css'
import { useModalDialog } from '../../../hooks/useModalDialog'

interface LoraShareModalProps {
    isOpen: boolean
    onClose: () => void
    loraId: string
    identityName: string
    canManage: boolean
    privacy: 'private' | 'public'
    onChanged?: () => Promise<void> | void
}

export function LoraShareModal({
    isOpen,
    onClose,
    loraId,
    identityName,
    canManage,
    privacy: initialPrivacy,
    onChanged,
}: LoraShareModalProps): ReactElement | null {
    const { t } = useTranslation('canvas')
    const [email, setEmail] = useState('')
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isMutating, setIsMutating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [collaborators, setCollaborators] = useState<LoraCollaboratorsResponse | null>(
        null
    )
    const [privacy, setPrivacy] = useState<'private' | 'public'>(initialPrivacy)
    const { dialogRef } = useModalDialog<HTMLDivElement>(isOpen, onClose)

    const ownerLabel = useMemo(() => {
        if (!collaborators?.owner) return t('sharing.owner')
        const owner = collaborators.owner
        return owner.display_name ?? owner.email ?? shortId(owner.id)
    }, [collaborators])

    async function refreshCollaborators(): Promise<void> {
        setError(null)
        setIsRefreshing(true)
        try {
            const data = await getLoraCollaborators(loraId)
            setCollaborators(data)
        } catch (e) {
            const err = e as Partial<HttpError>
            if (err.status === 404) {
                setError(t('sharing.loraNotFound'))
            } else {
                setError(t('sharing.failedToFetchCollaborators'))
            }
        } finally {
            setIsRefreshing(false)
        }
    }

    useEffect(() => {
        if (!isOpen) {
            // Avoid showing stale collaborator list on the next open.
            setCollaborators(null)
            setEmail('')
            setError(null)
            setIsRefreshing(false)
            setIsMutating(false)
            setPrivacy(initialPrivacy)
            return
        }

        // Reset immediately on loraId change to avoid flashing the previous list.
        setCollaborators(null)
        setError(null)
        setPrivacy(initialPrivacy)
        void refreshCollaborators()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, loraId, canManage])

    async function handleShare(): Promise<void> {
        const trimmed = email.trim().toLowerCase()
        if (!trimmed || isRefreshing || isMutating || !canManage) return
        if (privacy !== 'private') return

        if (!trimmed.includes('@') || trimmed.length < 3) {
            setError(t('sharing.invalidEmail'))
            return
        }

        setIsMutating(true)
        setError(null)
        try {
            await shareLora(loraId, trimmed)
            setEmail('')
            await refreshCollaborators()
            await onChanged?.()
        } catch (e) {
            const err = e as Partial<HttpError>
            if (err.status === 404) {
                setError(t('sharing.noUserFound'))
            } else if (err.status === 403) {
                setError(t('sharing.noPermissionShare'))
            } else {
                setError(t('sharing.failedToShare'))
            }
        } finally {
            setIsMutating(false)
        }
    }

    async function handleRemove(userId: string): Promise<void> {
        if (isRefreshing || isMutating || !canManage) return
        setIsMutating(true)
        setError(null)
        try {
            await unshareLora(loraId, userId)
            await refreshCollaborators()
            await onChanged?.()
        } catch (e) {
            const err = e as Partial<HttpError>
            if (err.status === 403) {
                setError(t('sharing.noPermissionUnshare'))
            } else {
                setError(t('sharing.failedToUnshare'))
            }
        } finally {
            setIsMutating(false)
        }
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            void handleShare()
        }
    }

    const shared = collaborators?.shared ?? []
    const isBusy = isRefreshing || isMutating

    async function handlePrivacyToggle(newPrivacy: string): Promise<void> {
        if (!canManage || isBusy) return
        if (newPrivacy !== 'private' && newPrivacy !== 'public') return

        setIsMutating(true)
        setError(null)
        try {
            await updateLoraPrivacy(loraId, newPrivacy)
            setPrivacy(newPrivacy)
            await onChanged?.()
        } catch (e) {
            const err = e as Partial<HttpError>
            if (err.status === 403) {
                setError(t('sharing.noPermissionPrivacy'))
            } else {
                setError(t('sharing.failedToUpdatePrivacy'))
            }
        } finally {
            setIsMutating(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="loraShareModalTitle"
                tabIndex={-1}
                className={styles.modal}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={styles.header}>
                    <h2 id="loraShareModalTitle" className={styles.title}>
                        {t('sharing.shareIdentity')}{' '}
                        <span className={styles.identityName}>{identityName}</span>
                    </h2>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={t('common:close')}
                    >
                        <CloseIcon size={20} />
                    </button>
                </header>

                <div className={styles.content}>
                    <div style={{ marginBottom: 'var(--space-sm)' }}>
                        <PrivacyToggle
                            privacy={privacy}
                            canEdit={canManage}
                            onToggle={handlePrivacyToggle}
                            entityName="identity"
                        />
                    </div>

                    <p className={styles.description}>
                        {canManage
                            ? t('sharing.shareDescription')
                            : t('sharing.collaboratorsDescription')}
                    </p>

                    {canManage && privacy === 'private' && (
                        <div className={styles.addSection}>
                            <input
                                className={styles.input}
                                type="email"
                                placeholder={t('sharing.enterEmailPlaceholder')}
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value)
                                    setError(null)
                                }}
                                onKeyDown={handleKeyDown}
                                disabled={isBusy}
                            />
                            <button
                                className={styles.addButton}
                                onClick={() => void handleShare()}
                                disabled={!email.trim() || isBusy}
                            >
                                {isMutating ? t('sharing.saving') : t('sharing.share')}
                            </button>
                        </div>
                    )}

                    {error && <div className={styles.error}>{error}</div>}

                    <div className={styles.list}>
                        <div className={styles.item}>
                            <div className={styles.userInfo}>
                                <UserIcon className={styles.userIcon} />
                                <span className={styles.userLabel}>{ownerLabel}</span>
                            </div>
                            <span className={styles.ownerBadge}>{t('sharing.owner')}</span>
                        </div>

                        {shared.map((u) => (
                            <div key={u.id} className={styles.item}>
                                <div className={styles.userInfo}>
                                    <UserIcon className={styles.userIcon} />
                                    <span className={styles.userLabel}>
                                        {u.display_name ?? u.email ?? shortId(u.id)}
                                    </span>
                                </div>
                                {canManage && (
                                    <button
                                        className={styles.removeButton}
                                        onClick={() => void handleRemove(u.id)}
                                        disabled={isBusy}
                                        title={t('sharing.removeUser')}
                                    >
                                        <TrashIcon size={16} />
                                    </button>
                                )}
                            </div>
                        ))}

                        {shared.length === 0 && (
                            <div className={styles.emptyList}>{t('sharing.noCollaborators')}</div>
                        )}

                        <div className={styles.footerRow}>
                            <button
                                className={styles.refreshButton}
                                onClick={() => void refreshCollaborators()}
                                disabled={isBusy}
                                title={t('sharing.refresh')}
                            >
                                <RefreshIcon size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function shortId(id: string): string {
    if (!id) return ''
    return id.length <= 8 ? id : id.slice(0, 8)
}


